import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Typography,
  notification,
} from "antd";
import queryString from "query-string";
import { createSpecialDeliveryOrder } from "../redux/actions/specialDeliveryActions";
import SpecialDeliveryNav from "./SpecialDeliveryNav";

const { Option } = Select;
const { Title, Paragraph } = Typography;

const subCategoryOptions = ["opening", "promo", "emergency", "vip"];

const extractErrorMessage = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  return data.message || "";
};

const SpecialDeliveries = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { authToken, clientId } = queryString.parse(location.search);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleError = useCallback(
    (error = {}) => {
      const { data = {} } = error;
      setSubmitError(
        extractErrorMessage(data) || "Special delivery tagging failed."
      );
    },
    []
  );

  const onFinish = (values) => {
    if (isSubmitting) {
      return;
    }

    setSubmitError("");

    const { subCategory, orderId, reasonCode } = values;

    const requestObject = {
      orderId: orderId?.trim(),
      subCategory,
      reasonCode: reasonCode?.trim(),
    };

    const authParams = { clientId, authToken };

    setIsSubmitting(true);

    dispatch(
      createSpecialDeliveryOrder(
        requestObject,
        authParams,
        navigate,
        handleError
      )
    )
      .then((result) => {
        if (result) {
          notification.success({
            message: "Order tagged as special delivery",
            description: `Order ${requestObject.orderId} is now a special delivery.`,
            placement: "bottomRight",
          });
          form.resetFields();
          return;
        }
        setSubmitError(
          (prev) => prev || "Could not tag this order. Please try again."
        );
      })
      .catch(() => {
        setSubmitError(
          (prev) => prev || "Could not tag this order. Please try again."
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0f2f5",
        padding: "32px 24px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: 640 }}>
        <SpecialDeliveryNav />
        <Card
          style={{ marginTop: 16, borderRadius: 8 }}
          bodyStyle={{ padding: 32 }}
        >
          <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
            Special Delivery Intake
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 24 }}>
            Tag an existing order as a special delivery so it can be planned
            separately.
          </Paragraph>

          {submitError ? (
            <Alert
              type="error"
              showIcon
              closable
              message={submitError}
              onClose={() => setSubmitError("")}
              style={{ marginBottom: 24 }}
            />
          ) : null}

          <Form
            form={form}
            layout="vertical"
            name="special-deliveries"
            size="middle"
            requiredMark={false}
            onFinish={onFinish}
          >
            <Form.Item
              label="Order ID"
              name="orderId"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter an order ID.",
                },
              ]}
            >
              <Input placeholder="e.g. Order-3" allowClear />
            </Form.Item>
            <Form.Item
              label="Sub-category"
              name="subCategory"
              rules={[
                { required: true, message: "Please select a sub-category." },
              ]}
            >
              <Select placeholder="Select a sub-category">
                {subCategoryOptions.map((subCategory) => (
                  <Option key={subCategory} value={subCategory}>
                    {subCategory}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Reason code"
              name="reasonCode"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter a reason code.",
                },
              ]}
            >
              <Input placeholder="Reason for special handling" allowClear />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Tag as special delivery
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default SpecialDeliveries;
